import React, { useState, useEffect } from 'react';
import { X, RefreshCw, ArrowLeftRight, ArrowRight } from 'lucide-react';

// Convert measurement using AI API (optional)
const convertMeasurementWithAI = async (ingredient, fromMeasurement, toType, targetLang = 'en', client = { region: 'israel' }) => {
  try {
    const response = await fetch('https://newdietitianweb-615263253386.europe-west3.run.app/api/convert-measurement', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ingredient: ingredient.item || ingredient.name,
        brand: ingredient['brand of pruduct'] || ingredient.brand || '',
        fromMeasurement,
        toType,
        targetLang,
        region: (client && client.region) ? client.region : 'israel'
      })
    });

    if (!response.ok) throw new Error(`Failed to convert measurement: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error in measurement conversion:', error);
    throw error;
  }
};

const IngredientPortionModal = ({ visible, onClose, onConfirm, ingredient, clientRegion, hideHouseholdMeasure = false, language = 'en' }) => {
  const [quantity, setQuantity] = useState(100);
  const [householdMeasure, setHouseholdMeasure] = useState('');
  const [convertingMeasure, setConvertingMeasure] = useState(false);
  const [notice, setNotice] = useState('');
  const say = (he, en) => setNotice(language === 'hebrew' ? he : en);

  // --- PREMIUM DESIGN TOKENS ---
  const isDarkMode = document.documentElement.classList.contains('dark');
  const themeClasses = {
    inputField: 'w-full rounded-md border border-line dark:border-line-dark bg-surface dark:bg-canvas-dark px-4 py-2.5 text-sm text-ink dark:text-white outline-none placeholder:text-ink-muted dark:placeholder:text-ink-muted-dark transition-colors duration-120 focus:border-forest-ondark focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas-dark',
    btnPrimary: 'min-h-[44px] px-5 py-2.5 rounded-md font-semibold bg-forest-ondark text-white hover:bg-forest-ondark/90 dark:bg-forest dark:text-canvas-dark dark:hover:bg-forest-hover active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas-dark flex items-center justify-center gap-2 disabled:opacity-50 transition-all duration-200',
    btnSecondary: 'min-h-[44px] px-5 py-2.5 rounded-md font-semibold border border-line dark:border-line-dark bg-surface dark:bg-surface-dark text-ink dark:text-white hover:bg-moss dark:hover:bg-moss-dark active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas-dark flex items-center justify-center gap-2 disabled:opacity-50 transition-colors duration-120',
    btnInfo: `px-4 py-2 rounded-lg font-medium bg-progress text-white hover:bg-progress  active:scale-[0.98] flex items-center justify-center gap-1.5 disabled:opacity-50 transition-all duration-200 text-xs sm:text-sm whitespace-nowrap`,
  };

  useEffect(() => {
    if (visible && ingredient) {
      setQuantity(ingredient['portionSI(gram)'] || 100);
      setHouseholdMeasure(ingredient.household_measure || '');
      setConvertingMeasure(false);
      setNotice('');
    }
  }, [visible, ingredient]);

  const handleConvertToHousehold = async () => {
    if (!ingredient || !quantity || quantity <= 0) {
      say('אנא הזן כמות בגרם', 'Please enter a quantity in grams');
      return;
    }

    try {
      setConvertingMeasure(true);
      const result = await convertMeasurementWithAI(
        ingredient,
        `${quantity}g`,
        'household',
        language === 'hebrew' ? 'he' : 'en',
        { region: clientRegion || 'israel' }
      );

      const measure = result.converted_measurement || result.household_measure || result.measure || result.text || '';
      if (measure && measure.trim()) {
        setHouseholdMeasure(measure.trim());
      } else {
        say('לא ניתן להמיר את המידה', 'Could not convert measurement');
      }
    } catch (error) {
      console.error('Error converting measurement:', error);
      say('שגיאה בהמרת המידה. נסה שוב.', 'Error converting measurement. Please try again.');
    } finally {
      setConvertingMeasure(false);
    }
  };

  const handleConvertToGrams = async () => {
    if (!ingredient || !householdMeasure || !householdMeasure.trim()) {
      say('אנא הזן מידה ביתית', 'Please enter a household measure');
      return;
    }

    try {
      setConvertingMeasure(true);
      const result = await convertMeasurementWithAI(
        ingredient,
        householdMeasure.trim(),
        'grams',
        language === 'hebrew' ? 'he' : 'en',
        { region: clientRegion || 'israel' }
      );

      const gramsText = result.converted_measurement || result.grams || result.measure || result.text || '';
      if (gramsText) {
        const gramsMatch = gramsText.toString().match(/(\d+(?:\.\d+)?)/);
        if (gramsMatch) {
          setQuantity(parseFloat(gramsMatch[1]));
        } else {
          say('לא ניתן להמיר את המידה', 'Could not convert measurement');
        }
      }
    } catch (error) {
      console.error('Error converting measurement:', error);
      say('שגיאה בהמרת המידה. נסה שוב.', 'Error converting measurement. Please try again.');
    } finally {
      setConvertingMeasure(false);
    }
  };

  const handleConfirm = () => {
    if (!ingredient) return;
    onConfirm({ quantity, householdMeasure });
  };

  if (!visible || !ingredient) return null;

  // Calculate original 100g values
  const originalScale = (ingredient['portionSI(gram)'] || 100) / 100;
  const original100gCalories = (ingredient.calories || 0) / originalScale;
  const original100gProtein = (ingredient.protein || 0) / originalScale;
  const original100gCarbs = (ingredient.carbs || 0) / originalScale;
  const original100gFat = (ingredient.fat || 0) / originalScale;

  // Calculate new values
  const newScale = quantity / 100;
  const newCalories = Math.round(original100gCalories * newScale);
  const newProtein = Math.round(original100gProtein * newScale * 10) / 10;
  const newCarbs = Math.round(original100gCarbs * newScale * 10) / 10;
  const newFat = Math.round(original100gFat * newScale * 10) / 10;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-canvas-dark/60 sm:items-center sm:p-4">
      <div className={`bg-surface dark:bg-surface-dark rounded-t-xl sm:rounded-lg w-full max-w-md max-h-[92dvh] overflow-y-auto flex flex-col border ${isDarkMode ? 'border-line-dark/60' : 'border-line/80'}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-line dark:border-line-dark/80">
          <div className="min-w-0 pr-4">
            <h2 className="text-ink dark:text-white text-xl sm:text-2xl font-semibold tracking-tight mb-1 truncate">
              {ingredient.item || ingredient.name || 'Unknown item'}
            </h2>
            <p className="text-ink-muted dark:text-ink-muted text-sm font-medium">
              {language === 'hebrew' ? 'ערוך כמות' : 'Edit Portion'}
            </p>
          </div>
          <button onClick={onClose} className="shrink-0 p-2 text-ink-muted hover:text-ink-muted dark:hover:text-ink-dark rounded-lg hover:bg-canvas dark:hover:bg-moss-dark/50 transition-colors">
            <X size={20} />
          </button>
        </div>

        {notice ? (
          <p className="px-5 sm:px-6 py-2 text-sm font-medium ind-fat bg-moss dark:bg-moss-dark" role="status">
            {notice}
          </p>
        ) : null}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          <div className="space-y-5">
            <div>
              <label className="text-ink-muted dark:text-ink-muted-dark block text-xs font-bold uppercase tracking-wider mb-2">
                {language === 'hebrew' ? 'כמות (גרם)' : 'Quantity (grams)'}
              </label>
              <div className="flex gap-3">
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
                  min="1"
                  className={themeClasses.inputField}
                />
                {!hideHouseholdMeasure && (
                  <button
                    onClick={handleConvertToHousehold}
                    disabled={convertingMeasure || !quantity || quantity <= 0}
                    className={themeClasses.btnInfo}
                  >
                    {convertingMeasure ? (
                      <RefreshCw size={16} className="animate-spin" />
                    ) : (
                      <>
                        <ArrowLeftRight size={14} />
                        <span>{language === 'hebrew' ? 'ביתית' : 'Household'}</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {!hideHouseholdMeasure && (
              <div>
                <label className="text-ink-muted dark:text-ink-muted-dark block text-xs font-bold uppercase tracking-wider mb-2">
                  {language === 'hebrew' ? 'מידה ביתית' : 'Household Measure'}
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={householdMeasure}
                    onChange={(e) => setHouseholdMeasure(e.target.value)}
                    placeholder={language === 'hebrew' ? 'לדוגמה: 1 כוס, 2 כפות' : 'e.g., 1 cup, 2 tbsp'}
                    className={themeClasses.inputField}
                  />
                  <button
                    onClick={handleConvertToGrams}
                    disabled={convertingMeasure || !householdMeasure || !householdMeasure.trim()}
                    className={themeClasses.btnInfo}
                  >
                    {convertingMeasure ? (
                      <RefreshCw size={16} className="animate-spin" />
                    ) : (
                      <>
                        <ArrowLeftRight size={14} />
                        <span>{language === 'hebrew' ? 'גרם' : 'Grams'}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Current vs New Nutrition Card */}
            <div className={`rounded-lg p-5 border bg-surface dark:bg-canvas-dark/30 ${isDarkMode ? 'border-line-dark' : 'border-line'}`}>
              <p className="text-ink-muted dark:text-ink-muted text-xs font-bold uppercase tracking-wider mb-4">
                {language === 'hebrew' ? 'ערכים תזונתיים' : 'Nutritional Values'}
              </p>
              
              <div className="space-y-3 text-sm font-semibold">
                <div className="flex items-center justify-between border-b border-canvas dark:border-surface-dark pb-2">
                  <span className="text-ink-muted dark:text-ink-muted">
                    {language === 'hebrew' ? 'קלוריות: ' : 'Calories: '}
                  </span>
                  <div className="flex items-center gap-2.5">
                    <span className="text-ink-muted dark:text-ink-muted line-through opacity-70">{ingredient.calories || 0}</span>
                    <ArrowRight size={14} className="text-ink-muted-dark dark:text-ink-muted" />
                    <span className="text-progress dark:text-progress font-bold bg-progress-faint dark:bg-progress/15 px-2 py-0.5 rounded border border-progress/30 dark:border-progress/50">{newCalories}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between border-b border-canvas dark:border-surface-dark pb-2">
                  <span className="text-ink-muted dark:text-ink-muted">
                    {language === 'hebrew' ? 'חלבון: ' : 'Protein: '}
                  </span>
                  <div className="flex items-center gap-2.5">
                    <span className="text-ink-muted dark:text-ink-muted line-through opacity-70">{ingredient.protein || 0}g</span>
                    <ArrowRight size={14} className="text-ink-muted-dark dark:text-ink-muted" />
                    <span className="text-forest-ondark dark:text-forest font-bold bg-forest-faint dark:bg-forest-faint-dark px-2 py-0.5 rounded border border-forest/20">{newProtein}g</span>
                  </div>
                </div>
                <div className="flex items-center justify-between border-b border-canvas dark:border-surface-dark pb-2">
                  <span className="text-ink-muted dark:text-ink-muted">
                    {language === 'hebrew' ? 'פחמימות: ' : 'Carbs: '}
                  </span>
                  <div className="flex items-center gap-2.5">
                    <span className="text-ink-muted dark:text-ink-muted line-through opacity-70">{ingredient.carbs || 0}g</span>
                    <ArrowRight size={14} className="text-ink-muted-dark dark:text-ink-muted" />
                    <span className="text-progress dark:text-progress font-bold bg-progress-faint dark:bg-progress/15 px-2 py-0.5 rounded border border-progress/30 dark:border-progress/50">{newCarbs}g</span>
                  </div>
                </div>
                <div className="flex items-center justify-between pb-1">
                  <span className="text-ink-muted dark:text-ink-muted">
                    {language === 'hebrew' ? 'שומן: ' : 'Fat: '}
                  </span>
                  <div className="flex items-center gap-2.5">
                    <span className="text-ink-muted dark:text-ink-muted line-through opacity-70">{ingredient.fat || 0}g</span>
                    <ArrowRight size={14} className="text-ink-muted-dark dark:text-ink-muted" />
                    <span className="text-amber-600 dark:text-amber-400 font-bold bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded border border-amber-100 dark:border-amber-800/50">{newFat}g</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 sm:p-6 border-t border-line dark:border-line-dark/80 bg-canvas/50 dark:bg-canvas-dark/30">
          <button
            onClick={onClose}
            className={themeClasses.btnSecondary}
          >
            {language === 'hebrew' ? 'ביטול' : 'Cancel'}
          </button>
          <button
            onClick={handleConfirm}
            className={themeClasses.btnPrimary}
          >
            {language === 'hebrew' ? 'שמור' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IngredientPortionModal;